import React, { useState, useContext, useEffect } from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Plus, Building, MapPin } from 'lucide-react';

const BuildingIntelligenceList = () => {
  const { token } = useContext(AuthContext);
  const navigate = useNavigate();
  const [intelligenceReports, setIntelligenceReports] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIntelligenceReports();
  }, []);

  const fetchIntelligenceReports = async () => {
    try {
      const response = await axios.get(`${API}/building-intelligence`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIntelligenceReports(response.data);
    } catch (error) {
      toast.error('Failed to load building intelligence reports');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getPropertyTypeColor = (type) => {
    const colors = {
      'bank': 'bg-blue-100 text-blue-800',
      'office': 'bg-purple-100 text-purple-800',
      'school': 'bg-green-100 text-green-800',
      'retail': 'bg-orange-100 text-orange-800',
      'mixed_use': 'bg-pink-100 text-pink-800'
    };
    return colors[type] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <p>Loading building intelligence reports...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <Building className="h-6 w-6 text-purple-600" />
          <h2 className="text-2xl font-bold">Building Intelligence</h2>
        </div>
        <Button onClick={() => navigate('/field/building-intelligence/new')} data-testid="new-building-intelligence-btn">
          <Plus className="h-4 w-4 mr-2" />
          New Report
        </Button>
      </div>

      {intelligenceReports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Building className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <p className="text-gray-500 mb-4">No building intelligence reports found</p>
            <Button onClick={() => navigate('/field/building-intelligence/new')}>
              <Plus className="h-4 w-4 mr-2" />
              Create First Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {intelligenceReports.map((report) => (
            <Card key={report.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => navigate(`/field/building-intelligence/${report.id}`)}>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <CardTitle className="text-lg mb-2">{report.property_name}</CardTitle>
                    <div className="flex items-center space-x-2 text-sm text-gray-600 mb-2">
                      <MapPin className="h-4 w-4" />
                      <span>{report.address}</span>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm text-gray-600">
                      <span><strong>Entry Date:</strong> {_fmtDate(report.date_of_entry)}</span>
                      <span>•</span>
                      <span><strong>Inspector:</strong> {report.inspector_name}</span>
                    </div>
                  </div>
                  <Badge className={getPropertyTypeColor(report.property_type)}>
                    {report.property_type.replace('_', ' ').toUpperCase()}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <div className="text-sm text-gray-600">
                  <div className="grid grid-cols-2 gap-4 mb-2">
                    {report.property_profile?.num_floors && (
                      <div>
                        <span className="text-gray-500">Floors:</span>
                        <span className="ml-2 font-medium">{report.property_profile.num_floors}</span>
                      </div>
                    )}
                    {report.interior_intelligence && report.interior_intelligence.length > 0 && (
                      <div>
                        <span className="text-gray-500">Floor Reports:</span>
                        <span className="ml-2 font-medium">{report.interior_intelligence.length}</span>
                      </div>
                    )}
                  </div>
                  {report.special_instructions && (
                    <p className="text-xs text-gray-500 mt-2">
                      {report.special_instructions.substring(0, 120)}{report.special_instructions.length > 120 ? '...' : ''}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 text-xs text-gray-500 mt-2">
                    {report.photos && report.photos.length > 0 && (
                      <span><strong>Photos:</strong> {report.photos.length}</span>
                    )}
                    <span><strong>Submitted:</strong> {_fmtDate(report.submitted_at)}</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default BuildingIntelligenceList;
